import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import DashboardKpiCard, { DashboardKpiRow } from "./DashboardKpiCard";
import DashboardMoeSection from "./DashboardMoeSection";
import DashboardMorSection from "./DashboardMorSection";

import { useI18n } from "../i18n/I18nContext";

const COLORS = {
  moe: "#378ADD",
  mor: "#639922",
  moy: "#E6FF3E",
  gray: "#94a3b8",
  blue: "#60a5fa",
};

function latestGlobalMonth(data) {
  const rows = data?.aggregates_month?.global || data?.aggregates?.global || [];
  return rows.length ? rows[rows.length - 1] : null;
}

export default function DashboardMoeMorPanel({ active }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [moeData, setMoeData] = useState(null);
  const [morData, setMorData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [moeRes, morRes] = await Promise.all([
          apiFetch("/api/dashboard/moe"),
          apiFetch("/api/dashboard/mor"),
        ]);
        if (!moeRes.ok || !morRes.ok) {
          throw new Error(`HTTP ${!moeRes.ok ? moeRes.status : morRes.status}`);
        }
        const [moeJson, morJson] = await Promise.all([moeRes.json(), morRes.json()]);
        if (moeJson.status === "error") throw new Error(moeJson.message || "Erreur MOE");
        if (morJson.status === "error") throw new Error(morJson.message || "Erreur MOR");
        if (!cancelled) {
          setMoeData(moeJson);
          setMorData(morJson);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur chargement MOE/MOR");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, loaded]);

  const kpis = useMemo(() => {
    const moeRow = latestGlobalMonth(moeData);
    const morRow = latestGlobalMonth(morData);
    return {
      moeE005: moeRow?.e005,
      moeMoy: moeRow?.moyenne,
      moeStacks: moeRow?.n_produits ?? 0,
      morFmk: morRow?.fmk,
      morMoy: morRow?.moyenne,
      morStacks: morRow?.n_produits ?? 0,
      moePeriod: moeRow?.periode ?? moeRow?.mois ?? "—",
      morPeriod: morRow?.periode ?? morRow?.mois ?? "—",
      totalRecords: (moeData?.records?.length ?? 0) + (morData?.records?.length ?? 0),
    };
  }, [moeData, morData]);

  if (!active) return null;

  return (
    <>
      {loading && (
        <div style={{ color: COLORS.moy, padding: 20, textAlign: "center" }}>Chargement MOE / MOR…</div>
      )}
      {error && (
        <div style={{ color: "#f87171", padding: 20, textAlign: "center" }}>{error}</div>
      )}
      {loaded && !error && (
        <>
          <DashboardKpiRow>
            <DashboardKpiCard
              label="MOE E0,05 (dernier mois)"
              value={kpis.moeE005 != null ? `${kpis.moeE005} GPa` : "N/A"}
              sub={`Global · ${kpis.moePeriod}`}
              color={COLORS.moe}
            />
            <DashboardKpiCard
              label="Moyenne MOE"
              value={kpis.moeMoy != null ? `${kpis.moeMoy} GPa` : "N/A"}
              sub={`${kpis.moeStacks} stack(s) produits`}
              color={COLORS.blue}
            />
            <DashboardKpiCard
              label="MOR fm,k (dernier mois)"
              value={kpis.morFmk != null ? `${kpis.morFmk} MPa` : "N/A"}
              sub={`Global · ${kpis.morPeriod}`}
              color={COLORS.mor}
            />
            <DashboardKpiCard
              label="Moyenne MOR"
              value={kpis.morMoy != null ? `${kpis.morMoy} MPa` : "N/A"}
              sub={`${kpis.morStacks} stack(s) produits`}
              color={COLORS.moy}
            />
          </DashboardKpiRow>
          <DashboardMoeSection atTop externalData={moeData} />
          <DashboardMorSection atTop externalData={morData} />
        </>
      )}
    </>
  );
}
